import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Trash2,
  ArrowRight,
  Sparkles,
  Calendar,
  Music,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import SEO from '../common/SEO';
import DraftDeleteConfirmModal from '../distribution/DraftDeleteConfirmModal';
import {
  fetchUserDrafts,
  deleteDistributionDraft,
  cleanupUserDrafts,
  generateUUID,
  clearActiveDistributionDraftId,
  setActiveDistributionDraftId,
} from '../../lib/distributionService';
import '../../styles/DraftsPage.css';

export default function DraftsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [cleanMessage, setCleanMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');

  // Draft deletion confirmation state
  const [draftToDelete, setDraftToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadDrafts = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchUserDrafts(user.id);
      setDrafts(data);
    } catch (err) {
      console.warn('[DraftsPage] Fetch error:', err);
      setError('Could not load drafts. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const handleRequestDelete = (draft, e) => {
    e.stopPropagation();
    setDraftToDelete(draft);
  };

  const handleCancelDelete = () => {
    if (!isDeleting) {
      setDraftToDelete(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!draftToDelete || !user?.id || isDeleting) return;

    setIsDeleting(true);
    setSuccessMessage('');
    setError('');

    try {
      await deleteDistributionDraft(draftToDelete.id, user.id);
      setDrafts((prev) => prev.filter((d) => d.id !== draftToDelete.id));
      setSuccessMessage('Draft deleted successfully.');
      setTimeout(() => setSuccessMessage(''), 4000);
      setDraftToDelete(null);
    } catch (err) {
      console.warn('[DraftsPage] Delete error:', err);
      setError('Unable to delete draft. Please try again.');
      setTimeout(() => setError(''), 4000);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCleanDuplicates = async () => {
    if (!user?.id || cleaning) return;
    setCleaning(true);
    setCleanMessage('');
    try {
      const { cleanedCount } = await cleanupUserDrafts(user.id);
      if (cleanedCount > 0) {
        setCleanMessage(`Successfully consolidated ${cleanedCount} duplicate draft${cleanedCount > 1 ? 's' : ''}.`);
      } else {
        setCleanMessage('No duplicate drafts found. All your drafts are unique.');
      }
      await loadDrafts();
      setTimeout(() => setCleanMessage(''), 4000);
    } catch (err) {
      console.warn('[DraftsPage] Cleanup error:', err);
    } finally {
      setCleaning(false);
    }
  };

  const getStepName = (step) => {
    switch (step) {
      case 1:
        return 'Release Type';
      case 2:
        return 'Audio Upload';
      case 3:
        return 'Track Details';
      case 4:
        return 'Cover Art';
      case 5:
        return 'Final Review';
      default:
        return 'Draft';
    }
  };

  const handleStartNewRelease = () => {
    clearActiveDistributionDraftId();
    const newId = generateUUID();
    setActiveDistributionDraftId(newId);
    navigate(`/distribution?draft_id=${newId}&mode=new`);
  };

  return (
    <div className="drafts-page-container">
      <SEO
        title="Release Drafts - SenritsuX Artist Web"
        description="Manage, continue, and organize your music release drafts on SenritsuX."
      />
      <Navbar />

      <main className="drafts-main-content">
        <div className="drafts-page-layout">
          {/* Left Column: Title Artwork + New Release Card (matches 15.webp) */}
          <div className="drafts-left-col">
            <div className="drafts-title-box">
              <img
                src="/assets/distribution/draft-page-title.webp"
                alt="Your Release Draft"
                className="drafts-title-img"
                onError={(e) => {
                  e.target.style.display = 'none';
                  const fallback = e.target.nextSibling;
                  if (fallback) fallback.style.display = 'block';
                }}
              />
              <div className="drafts-title-fallback" style={{ display: 'none' }}>
                <span className="drafts-title-fallback-top">YOUR RELEASE</span>
                <span className="drafts-title-fallback-bottom">DRAFT</span>
              </div>
            </div>

            {/* Release Sheet / New Release Card */}
            <div
              className="draft-new-release-card"
              onClick={handleStartNewRelease}
              role="button"
              tabIndex={0}
              aria-label="Create New Music Release"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleStartNewRelease();
                }
              }}
            >
              <div className="draft-new-release-circle">
                <Plus size={34} strokeWidth={2.8} />
              </div>
              <span className="draft-new-release-label">NEW RELEASE</span>
            </div>
          </div>

          {/* Right Column: Drafts Cards Grid */}
          <div className="drafts-right-col">
            {/* Notifications / Alerts */}
            {successMessage && (
              <div className="dist-status-banner success" style={{ animation: 'distModalFadeIn 0.25s ease-out' }}>
                <CheckCircle2 size={16} /> {successMessage}
              </div>
            )}
            {cleanMessage && (
              <div className="dist-status-banner info">
                <CheckCircle2 size={16} /> {cleanMessage}
              </div>
            )}
            {error && (
              <div className="dist-status-banner error">
                <AlertCircle size={16} /> {error}
              </div>
            )}

            {/* Optional Clean Duplicates button if user has multiple drafts */}
            {drafts.length > 1 && (
              <div className="drafts-header-actions">
                <button
                  type="button"
                  className="drafts-clean-btn"
                  onClick={handleCleanDuplicates}
                  disabled={cleaning}
                >
                  <RefreshCw size={13} className={cleaning ? 'animate-spin' : ''} />
                  {cleaning ? 'Consolidating...' : 'Clean Duplicates'}
                </button>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div style={{ textAlign: 'center', padding: '4rem 1rem', color: 'rgba(255,255,255,0.7)' }}>
                <RefreshCw size={30} className="animate-spin" style={{ margin: '0 auto 1rem', color: '#d9fb06' }} />
                <p>Loading your release drafts...</p>
              </div>
            )}

            {/* Empty State */}
            {!loading && drafts.length === 0 && (
              <div className="drafts-empty-card">
                <Sparkles size={36} color="#d9fb06" style={{ margin: '0 auto 0.8rem' }} />
                <h3 className="drafts-empty-title">No Saved Drafts</h3>
                <p className="drafts-empty-desc">
                  You do not have any release drafts in progress right now. Click "NEW RELEASE" to begin your music distribution!
                </p>
              </div>
            )}

            {/* Draft Cards Grid (matching Draft sheet.webp) */}
            {!loading && drafts.length > 0 && (
              <div className="drafts-cards-grid">
                {drafts.map((draft) => {
                  const d = draft.draftData || {};
                  const title = draft.title || d.title || 'Untitled Release';
                  const rawType = (draft.releaseType || d.releaseType || 'single').toUpperCase();
                  const step = draft.currentStep || d.currentStep || 1;
                  const trackCount = d.tracks?.length || d.audioTracks?.length || 1;
                  const formattedDate = draft.updatedAt
                    ? new Date(draft.updatedAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'Recently';

                  return (
                    <div
                      key={draft.id}
                      className="draft-card-item"
                      onClick={() => navigate(`/distribution?draft_id=${draft.id}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') navigate(`/distribution?draft_id=${draft.id}`);
                      }}
                    >
                      {/* Top-Right Trash Pill Tab */}
                      <button
                        type="button"
                        className="draft-trash-pill"
                        onClick={(e) => handleRequestDelete(draft, e)}
                        title="Delete Draft"
                        aria-label={`Delete ${title}`}
                      >
                        <Trash2 size={16} />
                      </button>

                      {/* Content Area */}
                      <div>
                        <h3 className="draft-card-title" title={title}>
                          {title}
                        </h3>

                        {/* Meta Row: Track Count + Step */}
                        <div className="draft-meta-row">
                          <Music size={14} className="draft-meta-icon" />
                          <span className="draft-track-count">
                            {trackCount} {trackCount === 1 ? 'Track' : 'Tracks'}
                          </span>
                          <span className="draft-meta-dot">•</span>
                          <span className="draft-step-text">
                            Step {step} Of 5 {getStepName(step)}
                          </span>
                        </div>

                        {/* Meta Row: Date */}
                        <div className="draft-meta-row">
                          <Calendar size={13} className="draft-meta-icon" />
                          <span className="draft-date-text">{formattedDate}</span>
                        </div>
                      </div>

                      {/* Bottom Row with Hanging Badges */}
                      <div className="draft-card-bottom-row">
                        {/* Type Badge (SINGLE / EP / ALBUM) with black dashed border */}
                        <div className="draft-type-badge">
                          {rawType}
                        </div>

                        {/* Circular Arrow Resume Button */}
                        <div
                          className="draft-arrow-btn"
                          title="Resume Draft"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/distribution?draft_id=${draft.id}`);
                          }}
                        >
                          <ArrowRight size={22} strokeWidth={2.8} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Branded In-Site Draft Delete Confirmation Modal */}
        <DraftDeleteConfirmModal
          isOpen={Boolean(draftToDelete)}
          draftTitle={draftToDelete?.title || draftToDelete?.draftData?.title || 'Untitled Release'}
          isDeleting={isDeleting}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </main>

      <Footer />
    </div>
  );
}
